import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FloppyDisk, PencilSimple } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';

const API = process.env.REACT_APP_BACKEND_URL;
const PROFILE_LABELS = {
  geral: 'Geral', gravida: 'Grávidas', pos_parto: 'Pós-parto',
  introducao_alimentar: 'Introdução alimentar', emagrecimento: 'Emagrecimento', pre_concecao: 'Pré-conceção',
};
const FIELD_LABELS = {
  greeting: 'Mensagem de boas-vindas', motivation: 'Texto motivacional',
  section_meals: 'Título secção refeições', section_progress: 'Título secção progresso',
};

export default function DashboardTextsPage() {
  useRequireAdmin();
  const [texts, setTexts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/dashboard-texts`); setTexts(data); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleEdit = (profile) => {
    const t = texts.find(x => x.profile === profile) || {};
    setEditing(profile);
    setForm({ greeting: t.greeting || '', motivation: t.motivation || '', section_meals: t.section_meals || '', section_progress: t.section_progress || '' });
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/api/dashboard-texts/${editing}`, form);
      toast.success('Textos guardados');
      setEditing(null);
      fetch();
    } catch { toast.error('Erro ao guardar'); }
  };

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  return (
    <div className="fade-in" data-testid="dashboard-texts-page">
      <div className="mb-5">
        <h2 className="font-heading font-medium text-xl text-brand-text">Textos do Dashboard</h2>
        <p className="text-xs text-brand-muted mt-1">Edita as mensagens visíveis para as clientes por perfil</p>
      </div>

      <div className="space-y-3">
        {texts.map(t => {
          const isEditing = editing === t.profile;
          return (
            <div key={t.profile} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`text-profile-${t.profile}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-medium text-sm text-brand-text">{PROFILE_LABELS[t.profile] || t.profile}</h3>
                {!isEditing && (
                  <button onClick={() => handleEdit(t.profile)} className="text-brand-green hover:text-brand-green-hover p-1" data-testid={`edit-texts-${t.profile}`}>
                    <PencilSimple size={16} />
                  </button>
                )}
              </div>

              {isEditing ? (
                <div className="space-y-3">
                  {Object.entries(FIELD_LABELS).map(([key, label]) => (
                    <div key={key}>
                      <Label className="text-xs text-brand-muted mb-1 block">{label}</Label>
                      <Input value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} className="rounded-xl border-brand-border text-sm" data-testid={`input-${key}-${t.profile}`} />
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <button onClick={handleSave} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1" data-testid={`save-texts-${t.profile}`}>
                      <FloppyDisk size={14} /> Guardar
                    </button>
                    <button onClick={() => setEditing(null)} className="text-brand-muted border border-brand-border rounded-full px-4 py-2 text-xs">Cancelar</button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {Object.entries(FIELD_LABELS).map(([key, label]) => (
                    <div key={key} className="flex items-start gap-2">
                      <span className="text-[10px] text-brand-muted w-32 flex-shrink-0">{label}:</span>
                      <span className="text-xs text-brand-text">{t[key] || '-'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
